<template>
    <page :title="isEditMode ? 'updateBestListTitle' : 'newBestListTitle'" :textHeight="getWindowHeight">
        <template #pageContainer>
            <form v-if="!adjusCombinationsMode && !adjusValueMode">
				<v-row><v-col>
					<v-text-field outlined dense
						hide-details="auto"
						:label="$t('text.name')"
						v-model="name"
						:error-messages="nameErrors"
						required
						@input="$v.name.$touch()"
						@blur="$v.name.$touch()">
					</v-text-field>
				</v-col></v-row>
				<v-row><v-col>
					<v-autocomplete outlined dense
						:search-input.sync="organization.orgsearch"
						hide-details="auto"
						:disabled="loading"
						clearable
						:no-data-text="$t('text.startTypingSearch')"
						@keyup="getFilteredOrganizations"
						v-model="organization.selected"
						:items="organization.items"
						:label="$t('text.regionalRestriction')"
						:loading="orgLoading">
					</v-autocomplete>
				</v-col></v-row>
				<custom-def-base
					@onPresetSelected="onCustomDefSelected">
				</custom-def-base>
            </form>

			<custom-def-combinations
				v-if="adjusCombinationsMode && !adjusValueMode"
				@onlaodingfinished="subLoadingFinished"
				@onlaodingstarted="subLoadingStarted"
				@oncreateeventclassdef="createEventClassDef">
			</custom-def-combinations>

			<custom-def-values
				v-if="!adjusCombinationsMode && adjusValueMode && !!customDefs"
				:customEventClasses="customDefs"
				@onupdatecustomdef="updateCustomDef"
				@onlaodingfinished="subLoadingFinished"
				@onlaodingstarted="subLoadingStarted">
			</custom-def-values>
            <spinner :loading="loading || subLoading"></spinner>
        </template>
		<template #pageActions v-if="!adjusCombinationsMode && !adjusValueMode">
			<v-row><v-col>
				<v-btn v-if="!isEditMode"
					class="mr-4"
					:loading="loading"
					:disabled="$v.$invalid || !isCustomDefValide || loading"
					@click="createAndBack">
					{{ $t(`text.createButton`) }}
				</v-btn>
				<v-btn v-else
					class="mr-4"
					:loading="loading"
					:disabled="$v.$invalid || !isCustomDefValide || loading"
					@click="createAndBack">
					{{ $t(`text.updateButton`) }}
				</v-btn>
				<v-btn
					class="mr-4"
					:loading="defsLoading"
					:disabled="$v.$invalid || !isCustomDefValide || loading"
					@click="createAndAdjust">
					{{ $t(`text.adjustDef`) }}
				</v-btn>

				<v-btn
					:disabled="loading"
					class="mr-4"
					@click="back">
					{{ $t(`text.backButton`) }}
				</v-btn>
			</v-col></v-row>
		</template>
    </page>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';

import Page from '../general/Page.vue';
import Spinner from '../general/Spinner.vue';
import CustomDefBase from './CustomDefBase.vue';
import CustomDefCombinations from './CustomDefCombinations.vue';
import CustomDefValues from './CustomDefValues.vue';

import bestListActions from '../store/actions/bestList';
import namespace from '../store/namespace';

import modelState from '../mixins/modelState';

export default {
	name: 'NewBestlist',
	components: {
		Page,
		Spinner,
		CustomDefBase,
		CustomDefCombinations,
		CustomDefValues,
	},
	mixins: [modelState],
	validations: {
		name: {
			required,
		},
	},
	data() {
		return {
			name: '',
			loading: false,
			subLoading: false,
			organization: {
				items: [],
				selected: null,
				orgsearch: '',
			},
			defsLoading: false,
			orgLoading: false,
			customDefValide: true,
			selectedPreset: null,
			selectedBaseDef: null,
			customDefs: null,
			id: this.$route.params.id || null,
			adjusCombinationsMode: ['customDefinitions', 'customDefinitionsMatrix'].includes(this.$route.name),
			adjusValueMode: this.$route.name == 'customDefinitionsValues',
		};
	},
	watch: {
		'$route.name': function () {
			this.adjusCombinationsMode = ['customDefinitions', 'customDefinitionsMatrix'].includes(this.$route.name);
			this.adjusValueMode = this.$route.name == 'customDefinitionsValues';
		},
	},
	computed: {
		...mapGetters(['WindowSize']),
		...mapGetters(namespace.USER, ['User']),
		...mapGetters(namespace.BESTLIST, ['Links', 'Entities']),
		...mapGetters(namespace.ORGANIZATION, ['HasOrganization', 'ExternalId']),
		getWindowHeight() {
			if (this.adjusCombinationsMode || this.adjusValueMode) return `${this.WindowSize.y - 216}px`;
			return `${this.WindowSize.y - 276}px`;
		},
		nameErrors() {
			const errors = [];

			if (!this.$v.name.$dirty) return errors;

			const apiErrors = this.validateProperty('Name');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.name.required && errors.push(this.$i18n.t('text.errorNameRequired'));

			return errors;
		},
		isCustomDefValide() {
			return this.customDefValide;
		},
		hasOnlyOneOrganization() {
			return this.organization.items.length === 1;
		},
		anyOrg() {
			return this.organization.items.length > 0;
		},
		isEditMode() {
			return !!this.id;
		},
	},
	async mounted() {
		if (!this.Links.length) {
			await this.GET_PERFORMANCELIST_LINKS();
		}
		if (this.isEditMode && this.id) {
			await this.getBestList();
			await this.getFilteredOrganizations(this.organization.selected);
			await this.getCustomDefs();
		}
	},
	methods: {
		...mapActions(namespace.BESTLIST, [
			bestListActions.CREATE_NEW_BEST_LIST,
			bestListActions.GET_BRANCHES_FOR_PERFDB,
			bestListActions.GET_BRANCH_BY_NAME_AND_MASTERID,
			bestListActions.GET_BESTLIST_BY_ID,
			bestListActions.FULL_UPDATE_BESTLIST,
			bestListActions.CREATE_DLO_DEFS,
			bestListActions.COPY_DEF_FROM_EXISTING,
			bestListActions.CREATE_EVENT_CLASS_ARRAY,
			bestListActions.GET_CUSTOM_DEFS,
			bestListActions.UPDATE_CUSTOM_DEF,
			bestListActions.GET_PERFORMANCELIST_LINKS,
		]),
		onCustomDefSelected(payload) {
			this.customDefValide = payload.valide;
			this.selectedDefBase = payload.selectedDefBase;
			this.selectedPreset = payload.selectedPreset;
		},
		async createEventClassDef(payload) {
			const link = this.Links.find(x => x.rel === 'newcustomeventdefinitions');
			try {
				await this.CREATE_EVENT_CLASS_ARRAY({ link, payload });
				await this.getCustomDefs();
			} finally {
				this.adjusValueMode = true;
				this.adjusCombinationsMode = false;
				this.$router.push({
					name: 'customDefinitionsValues',
					params: {
						id: this.id,
					},
				});
			}
		},
		async updateCustomDef(payload) {
			const link = this.Links.find(x => x.rel === 'updatecustomdefs');
			await this.UPDATE_CUSTOM_DEF({ link, payload });
			this.adjusValueMode = false;
			this.subLoadingFinished();
		},
		async getCustomDefs() {
			if (this.id != null) {
				const link = this.Links.find(x => x.rel === 'get_custom_defs');
				const href = link.href.replace('{performanceListId}', this.id);
				this.customDefs = await this.GET_CUSTOM_DEFS({ href });
			}
		},
		async getPerformanceListLinks() {
			try {
				await this.GET_PERFORMANCELIST_LINKS();
			} catch (ex) {
				console.log(ex);
			}
		},
		async createAndBack() {
			await this.create();
			this.$router.push({
				name: 'bestlist',
			});
		},
		async createAndAdjust() {
			this.defsLoading = true;
			this.subLoadingStarted();
			try {
				await this.create();
				await this.getCustomDefs();
			} finally {
				this.defsLoading = false;
				this.adjusCombinationsMode = true;
				this.$router.push({
					name: 'customDefinitions',
					params: {
						id: this.id,
					},
				});
			}
		},
		subLoadingStarted() {
			this.subLoading = true;
		},
		subLoadingFinished() {
			this.subLoading = false;
		},
		async create() {
			this.loading = true;
			this.$v.$touch();

			if (!this.$v.$invalid) {
				let link = null;
				let bestList = null;
				if (this.isEditMode) {
					link = this.Links.find(x => x.rel === 'update');
					bestList = {
						id: this.id,
						name: this.name,
						regionalRestritionId: this.organization.selected,
					};
				} else {
					link = this.Links.find(x => x.rel === 'new');
					bestList = {
						regionalRestritionId: this.organization.selected,
						ownerId: this.ExternalId,
						name: this.name,
					};
				}

				if (link) {
					try {
						this.modelState = {};
						if (this.isEditMode) {
							await this.FULL_UPDATE_BESTLIST({ bestList, link });
						} else {
							const payload = await this.CREATE_NEW_BEST_LIST({ bestList, link });
							this.id = payload.id;
							const targetId = payload.id;
							const srcId = this.selectedPreset;
							switch (this.selectedDefBase) {
							case 0:
								link = this.Links.find(x => x.rel === 'copy_dlo');
								await this.CREATE_DLO_DEFS({ link, targetId });
								break;
							case 1:
								link = this.Links.find(x => x.rel === 'copy_from_target');
								await this.COPY_DEF_FROM_EXISTING({ link, srcId, targetId });
								break;
							default:
							}
						}
					} catch (e) {
						if (e.response.status === 422) {
							this.modelState = e.response.data;
							if (this.modelError()) {
								throw new Error(this.modelError());
							}
						}

						throw e;
					} finally {
						this.loading = false;
					}
				}
			}
			this.loading = false;
		},
		back() {
			this.$router.push({
				name: 'bestlist',
			});
		},
		async getFilteredOrganizations(id) {
			if ((this.organization.orgsearch && this.organization.orgsearch.length == 3) || id) {
				this.orgLoading = true;
				try {
					this.organization.items = await this.GET_BRANCH_BY_NAME_AND_MASTERID(
						{
							id: this.User.masterOrganization.externalId,
							name: this.organization.orgsearch || id,
						},
					);

					if (this.organization.items.length === 1) {
						this.organization.selected = this.organization.items[0].value;
					}
				} finally {
					this.orgLoading = false;
				}
			}
		},
		async getBestList() {
			this.loading = true;
			try {
				const bestList = await this.GET_BESTLIST_BY_ID(this.id);

				if (bestList) {
					this.name = bestList.name;
					this.organization.selected = bestList.regionalRestritionId;
				}
			} finally {
				this.loading = false;
			}
		},
	},
};
</script>
